import { Context } from 'chartjs-plugin-datalabels';
import { Chart } from 'chart.js';
import { shuffle } from 'helpers';
import {
	IChartParams,
	IPointWithAlign,
	IPointWithAlignAndSize,
	IPointWithCoordinates,
	IPointWithLabelCoordinates,
	AlignTypes,
	IChartPoint,
	IXAxesMinMax,
} from '../../models';
import { getChartParams } from './chartFunctions';
import { getLabelIntersectionsWithChartBorders } from './checkIntersectionWithChartBorders';
import {
	checkPointLabelAndAnotherPointDotIntersection,
	checkPointLabelsIntersection,
} from './checkIntersection';
import {
	getPointCoordinates,
	getPointLabelCoordinates,
	getPointLabelSizes,
} from './getPointAndLabelParameters';

const START_CYCLE_NUMBER = 1;
const MAX_CYCLE_NUMBER = 5;

const setAlignAndReturnNewPoints = (
	pointsWithAlign: IPointWithAlignAndSize[],
	chartParams: IChartParams,
) => {
	let newPointsWithAlign = pointsWithAlign;
	let cycleNumber = 1;

	while (
		cycleNumber === START_CYCLE_NUMBER ||
		(newPointsWithAlign.find(({ isChanged }) => isChanged) &&
			cycleNumber <= MAX_CYCLE_NUMBER)
	) {
		newPointsWithAlign = newPointsWithAlign.reduce((res, point) => {
			const pointsWithoutCurrent = res.filter(
				({ label }) => point.label !== label,
			);
			const newAlign = checkIntersectionsAndGetNewAlign(
				chartParams,
				pointsWithoutCurrent,
				point,
			);

			return res.map((item) =>
				item.priority !== point.priority
					? item
					: {
							...item,
							align: newAlign,
							isChanged: item.align !== newAlign,
					  },
			);
		}, newPointsWithAlign);

		cycleNumber += 1;
	}

	return newPointsWithAlign;
};

export const getPointsWithAlign = (
	context: Context,
	minAndMaxTicksValues: IXAxesMinMax,
	chartData: IChartPoint[],
): IPointWithAlign[] => {
	const chartParams = getChartParams(context, minAndMaxTicksValues);
	const { chart } = chartParams;
	if (!chart.ctx) return [];

	const pointsWithBasicAlign: IPointWithAlignAndSize[] = chartData.map(
		(point) => ({
			...point,
			align: AlignTypes.bottom,
			isChanged: false,
			...getPointLabelSizes(point, chartParams),
		}),
	);

	return setAlignAndReturnNewPoints(pointsWithBasicAlign, chartParams);
};

const getPointsWithIntersection = (
	chartParams: IChartParams,
	allPoints: IPointWithCoordinates[],
	pointWithCoordinates: IPointWithLabelCoordinates,
) => {
	return allPoints.filter((item) => {
		const curPointLabelIntersectsPoint = checkPointLabelAndAnotherPointDotIntersection(
			pointWithCoordinates,
			item,
			chartParams,
		);
		if (item.align === AlignTypes.center) return curPointLabelIntersectsPoint;

		const areIntersected = checkPointLabelsIntersection(
			pointWithCoordinates,
			item,
		);
		return areIntersected || curPointLabelIntersectsPoint;
	});
};

const partiallyApplyGetPointsWithIntersection = (
	chartParams: IChartParams,
	allPoints: IPointWithCoordinates[],
	point: IPointWithAlignAndSize,
) => {
	return (alignType: AlignTypes) => {
		const pointLabelCoordinates = getPointLabelCoordinates(
			chartParams,
			point,
			alignType,
		);
		const pointWithCoordinates = {
			...point,
			...pointLabelCoordinates,
		};

		return getPointsWithIntersection(
			chartParams,
			allPoints,
			pointWithCoordinates,
		);
	};
};

const checkIntersectionsAndGetNewAlign = (
	chartParams: IChartParams,
	allPoints: IPointWithAlignAndSize[],
	point: IPointWithAlignAndSize,
) => {
	const allPointsWithLabelCoordinates: IPointWithCoordinates[] = allPoints.map(
		(item) => ({
			...item,
			...getPointLabelCoordinates(chartParams, item, item.align),
			...getPointCoordinates(chartParams, item),
		}),
	);
	const getPointsThatIntersectCurrentPoint = partiallyApplyGetPointsWithIntersection(
		chartParams,
		shuffle(allPointsWithLabelCoordinates),
		point,
	);

	const {
		bottomLabelIntersectsBorder,
		topLabelIntersectsBorder,
		rightLabelIntersectsBorder,
		leftLabelIntersectsBorder,
	} = getLabelIntersectionsWithChartBorders(chartParams, point);

	const bottomPoints = getPointsThatIntersectCurrentPoint(AlignTypes.bottom);
	if (!bottomPoints.length && !bottomLabelIntersectsBorder)
		return AlignTypes.bottom;

	const topPoints = getPointsThatIntersectCurrentPoint(AlignTypes.top);
	if (!topPoints.length && !topLabelIntersectsBorder) return AlignTypes.top;

	const rightPoints = getPointsThatIntersectCurrentPoint(AlignTypes.right);
	if (!rightPoints.length && !rightLabelIntersectsBorder)
		return AlignTypes.right;

	const leftPoints = getPointsThatIntersectCurrentPoint(AlignTypes.left);
	if (!leftPoints.length && !leftLabelIntersectsBorder) return AlignTypes.left;

	return AlignTypes.center;
};
